import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/lib/dashboard';
import { qaApi } from '@/lib/qa';
import { snapshotsApi } from '@/lib/snapshots';
import { TotalsByPeriodChart } from '@/components/charts/TotalsByPeriodChart';
import { KpiTiles } from '@/components/app/KpiTiles';
import { SignalsPanel } from '@/components/app/SignalsPanel';
import { NextStepsCard } from '@/components/app/NextStepsCard';
import { ActivationChecklist } from '@/components/app/ActivationChecklist';
import { VariancePanel } from '@/components/VariancePanel';
import { ConsolidationPanel } from '@/components/ConsolidationPanel';
import { AppShell, PageSection } from '@/components/app/AppShell';
import { formatMoney } from '@/lib/currency';
import { ConfirmModal } from '@/components/ConfirmModal';
import { sampleDataApi } from '@/lib/sampleData';
import { useToast, errMessage } from '@/components/Toast';

export function WorkspacePage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ account: string; scenario: string; ids: string[] } | null>(null);
  const [revokeId, setRevokeId] = useState<string | null>(null);

  const { data: snapshot, isLoading: snapLoading } = useQuery({
    queryKey: ['dashboard', wsId],
    queryFn: () => dashboardApi.snapshot(wsId!),
    enabled: !!wsId,
  });

  // One-click sample data (Wave U3). Refreshes every data-derived surface on
  // success so the dashboard, KPIs, signals and next-steps light up at once.
  const loadSample = useMutation({
    mutationFn: () => sampleDataApi.load(wsId!),
    onSuccess: (r) => {
      ['dashboard', 'kpi-dashboard', 'signals-dashboard', 'next-actions', 'activation', 'variances'].forEach((k) =>
        qc.invalidateQueries({ queryKey: [k, wsId] }),
      );
      toast.success(t('sample.loaded', { defaultValue: 'Loaded a sample company ({{n}} months of data). Explore away!', n: r.months }));
    },
    onError: (e) => toast.error(errMessage(e, t('sample.error', { defaultValue: 'Could not load sample data.' }))),
  });

  // The "Explore with sample data" next-action links here with ?sample=1.
  useEffect(() => {
    if (searchParams.get('sample') === '1' && snapshot && snapshot.totalFacts === 0 && !loadSample.isPending && !loadSample.isSuccess) {
      loadSample.mutate();
      setSearchParams((p) => { p.delete('sample'); return p; }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, snapshot]);

  const { data: snapshots = [] } = useQuery({
    queryKey: ['snapshots', wsId],
    queryFn: () => snapshotsApi.list(wsId!),
    enabled: !!wsId,
  });

  const revoke = useMutation({
    mutationFn: (id: string) => snapshotsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['snapshots', wsId] }),
  });

  const share = useMutation({
    mutationFn: () => snapshotsApi.create(wsId!, { ttlDays: 30 }),
    onSuccess: (snap) => {
      const url = snapshotsApi.publicUrl(snap.id);
      setShareUrl(url);
      void navigator.clipboard?.writeText(url).catch(() => undefined);
      qc.invalidateQueries({ queryKey: ['snapshots', wsId] });
    },
  });

  const subtitle = snapshot?.totalFacts
    ? t('workspace.facts_summary', { count: snapshot.totalFacts, accounts: snapshot.accountCount })
    : t('workspace.no_data');

  const actions = (
    <>
      <button
        onClick={() => share.mutate()}
        disabled={share.isPending || !snapshot || snapshot.totalFacts === 0}
        className="h-9 px-3.5 rounded-md border border-border text-[12.5px] font-semibold hover:bg-foreground/[0.04] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {share.isPending ? t('workspace.snapshotting') : t('workspace.share_snapshot')}
      </button>
      <Link
        to={`/app/workspace/${wsId}/upload`}
        className="h-9 px-3.5 inline-flex items-center rounded-md bg-foreground text-background text-[12.5px] font-semibold hover:bg-foreground/90 transition-colors"
      >
        {t('workspace.upload_data')}
      </Link>
    </>
  );

  return (
    <AppShell title={t('workspace.dashboard')} subtitle={subtitle} pageActions={actions}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {wsId && <ActivationChecklist wsId={wsId} />}
        {wsId && <NextStepsCard wsId={wsId} />}
        {shareUrl && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] text-emerald-800">
            {t('workspace.snapshot_copied')}{' '}
            <span className="num font-semibold break-all">{shareUrl}</span>
          </div>
        )}

        {snapshots.length > 0 && (
          <div className="rounded-md border border-border bg-white px-4 py-3">
            <h3 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium mb-2">
              {t('workspace.shared_snapshots', { defaultValue: 'Shared snapshots' })}
            </h3>
            <ul className="space-y-1.5">
              {snapshots.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-3 text-[12.5px]">
                  <a href={snapshotsApi.publicUrl(s.id)} target="_blank" rel="noreferrer" className="num text-emerald-700 hover:underline truncate">
                    /share/{s.id.slice(0, 8)}
                  </a>
                  <span className="num text-[11px] text-muted-foreground shrink-0">
                    {new Date(s.createdAt).toLocaleDateString()}{s.expiresAt ? ` · exp ${new Date(s.expiresAt).toLocaleDateString()}` : ''}
                  </span>
                  <button
                    onClick={() => setRevokeId(s.id)}
                    disabled={revoke.isPending}
                    className="text-[11px] text-ink-500 hover:text-warn-600 shrink-0 disabled:opacity-50"
                  >
                    {t('workspace.revoke', { defaultValue: 'Revoke' })}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {snapLoading && (
          <div className="text-muted-foreground text-sm">{t('common.loading')}</div>
        )}

        {!snapLoading && snapshot && snapshot.totalFacts === 0 && (
          <div className="rounded-md border border-dashed border-border bg-card p-12 text-center">
            <p className="text-foreground font-medium">{t('workspace.no_facts')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t('workspace.no_facts_sub')}</p>
            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
              <Link
                to={`/app/workspace/${wsId}/upload`}
                className="inline-flex items-center px-4 py-2 rounded-md bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
              >
                {t('workspace.upload_first')}
              </Link>
              <button
                onClick={() => loadSample.mutate()}
                disabled={loadSample.isPending}
                className="inline-flex items-center px-4 py-2 rounded-md border border-border text-sm font-semibold text-foreground hover:bg-foreground/[0.04] transition-colors disabled:opacity-60"
              >
                {loadSample.isPending
                  ? t('sample.loading', { defaultValue: 'Loading sample…' })
                  : t('sample.load', { defaultValue: 'Or explore with sample data' })}
              </button>
            </div>
          </div>
        )}

        {!snapLoading && snapshot && snapshot.totalFacts > 0 && (
          <>
            {wsId && <SignalsPanel wsId={wsId} />}
            {wsId && <KpiTiles wsId={wsId} />}
            <PageSection eyebrow={t('workspace.totals_title')} title={t('workspace.dashboard')}>
              <div className="rounded-md border border-border bg-card p-5">
                <TotalsByPeriodChart series={scenariosToSeries(snapshot)} />
                {snapshot.fx?.ratesUsed?.length > 0 && (
                  <p className="mt-3 text-[11px] text-muted-foreground num">
                    {t('workspace.fx_footnote', {
                      currency: snapshot.fx.reportingCurrency,
                      rates: snapshot.fx.ratesUsed.map((r) => `${r.from}→${r.to} ${r.rate.toFixed(4)} (${r.date})`).join(', '),
                    })}
                  </p>
                )}
                {snapshot.fx?.missingRates?.length > 0 && (
                  <div className="mt-1">
                    <p className="text-[11px] text-warn-700 num">
                      {t('workspace.fx_missing', { pairs: snapshot.fx.missingRates.join(', ') })}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {snapshot.fx.missingRates.map((pair) => {
                        const [from, to] = pair.split('→');
                        return (
                          <Link
                            key={pair}
                            to={`/app/workspace/${wsId}/fx-rates?base=${from ?? ''}&quote=${to ?? ''}`}
                            className="num text-[10.5px] px-1.5 py-0.5 rounded border border-warn-500/30 text-warn-700 hover:bg-warn-500/10"
                            title={t('approvals.open_target', { defaultValue: 'Add this rate' })}
                          >
                            + {pair}
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </PageSection>

            <PageSection eyebrow={t('variance.title')} title={t('variance.title')}>
              <VariancePanel workspaceId={wsId!} />
            </PageSection>

            <PageSection eyebrow={t('consolidation.title')} title={t('consolidation.title')}>
              <ConsolidationPanel workspaceId={wsId!} />
            </PageSection>

            <PageSection
              eyebrow={t('workspace.top_accounts', { count: snapshot.topAccounts.length })}
              title={t('workspace.top_accounts', { count: snapshot.topAccounts.length })}
            >
              <div className="rounded-md border border-border bg-card overflow-x-auto">
                <table className="w-full text-[13px] min-w-[520px]">
                  <thead>
                    <tr className="bg-ink-50/70 border-b border-border">
                      <th className="text-left px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {t('variance.th_account')}
                      </th>
                      <th className="text-left px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {t('common.scenario')}
                      </th>
                      <th className="text-right px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {t('common.total')}
                      </th>
                      <th className="text-right px-4 py-2 font-medium text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        {t('workspace.th_periods')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {snapshot.topAccounts.map((a) => (
                      <tr key={`${a.account_code}-${a.scenario}`} className="hover:bg-ink-50/40">
                        <td className="num px-4 py-2 font-medium">
                          {a.fact_ids && a.fact_ids.length > 0 ? (
                            <button
                              onClick={() => setDrill({ account: a.account_code, scenario: a.scenario, ids: a.fact_ids! })}
                              className="text-emerald-700 hover:text-emerald-800 hover:underline"
                              title={t('workspace.drill_hint')}
                            >
                              {a.account_code}
                            </button>
                          ) : (
                            a.account_code
                          )}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">{a.scenario}</td>
                        <td className="num px-4 py-2 text-right font-medium">
                          {formatMoney(a.total, { maximumFractionDigits: 0 })}
                        </td>
                        <td className="num px-4 py-2 text-right text-muted-foreground">
                          {a.points.length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </PageSection>
          </>
        )}
      </div>
      {drill && wsId && (
        <FactsDrawer
          workspaceId={wsId}
          account={drill.account}
          scenario={drill.scenario}
          ids={drill.ids}
          onClose={() => setDrill(null)}
          t={t}
        />
      )}
      <ConfirmModal
        open={revokeId !== null}
        title={t('workspace.revoke', { defaultValue: 'Revoke' })}
        body={t('workspace.revoke_confirm', { defaultValue: 'Revoke this share link?' })}
        confirmLabel={t('workspace.revoke', { defaultValue: 'Revoke' })}
        destructive
        onConfirm={() => { if (revokeId) revoke.mutate(revokeId); setRevokeId(null); }}
        onCancel={() => setRevokeId(null)}
      />
    </AppShell>
  );
}

function FactsDrawer({ workspaceId, account, scenario, ids, onClose, t }: {
  workspaceId: string;
  account: string;
  scenario: string;
  ids: string[];
  onClose: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const { data: facts, isLoading } = useQuery({
    queryKey: ['drill-facts', workspaceId, ids],
    queryFn: () => qaApi.facts(workspaceId, ids),
  });
  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-xl bg-white h-full shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border sticky top-0 bg-white">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t('workspace.facts_title')}</h3>
            <p className="num text-xs text-muted-foreground">{account} · {scenario}</p>
          </div>
          <button onClick={onClose} className="text-ink-500 hover:text-foreground text-sm px-2 py-1">✕</button>
        </div>
        {isLoading ? (
          <p className="p-5 text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : !facts || facts.length === 0 ? (
          <p className="p-5 text-sm text-muted-foreground">{t('workspace.facts_empty')}</p>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-ink-50/70 border-b border-border text-left">
                <th className="px-4 py-2 font-medium text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t('fx.date')}</th>
                <th className="px-4 py-2 font-medium text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t('common.scenario')}</th>
                <th className="px-4 py-2 font-medium text-[10px] uppercase tracking-[0.14em] text-muted-foreground text-right">{t('common.total')}</th>
                <th className="px-4 py-2 font-medium text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t('upload.source_row', { defaultValue: 'Source' })}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {facts.map((f) => (
                <tr key={f.factId}>
                  <td className="num px-4 py-1.5 text-ink-700">{f.periodStart}</td>
                  <td className="px-4 py-1.5 text-muted-foreground">{f.scenario}</td>
                  <td className="num px-4 py-1.5 text-right tabular-nums">{f.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                  <td className="num px-4 py-1.5 text-[11px] text-muted-foreground truncate max-w-[140px]">{f.sourceRowRef ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function scenariosToSeries(snap: { totalsByPeriod: { period_start: string; scenario: string; amount: number }[] }) {
  const byScenario = new Map<string, { x: string; y: number }[]>();
  for (const point of snap.totalsByPeriod) {
    let arr = byScenario.get(point.scenario);
    if (!arr) {
      arr = [];
      byScenario.set(point.scenario, arr);
    }
    arr.push({ x: point.period_start, y: point.amount });
  }
  return Array.from(byScenario.entries()).map(([name, data]) => ({ name, data }));
}
